// Survives Vite HMR + React StrictMode remounts so in-flight GETs stay shared.

export type TtlEntry<T> = { at: number; value: T };

type EaCacheBag = {
  inflight: Map<string, Promise<unknown>>;
  list: Map<string, TtlEntry<unknown>>;
  opportunity: Map<string, TtlEntry<unknown>>;
  evaluation: Map<string, TtlEntry<unknown>>;
  roi: Map<string, TtlEntry<unknown>>;
  selection: Map<string, TtlEntry<unknown>>;
  commandCenter: Map<string, TtlEntry<unknown>>;
  playbooks: Map<string, TtlEntry<unknown>>;
  scoring: Map<string, TtlEntry<unknown>>;
  pi: Map<string, TtlEntry<unknown>>;
  /**
   * DMD-1180: session-durable use-case header meta (id → title/category/…).
   * Survives the 60s list/detail TTL so prev/next never falls back to "Use Case N".
   */
  catalogMeta: Map<string, unknown>;
};

const STORE_KEY = '__dvaEaRequestCaches';

export function eaCaches(): EaCacheBag {
  const g = globalThis as typeof globalThis & { [STORE_KEY]?: EaCacheBag };
  if (!g[STORE_KEY]) {
    g[STORE_KEY] = {
      inflight: new Map(),
      list: new Map(),
      opportunity: new Map(),
      evaluation: new Map(),
      roi: new Map(),
      selection: new Map(),
      commandCenter: new Map(),
      playbooks: new Map(),
      scoring: new Map(),
      pi: new Map(),
      catalogMeta: new Map(),
    };
  }
  // HMR / older sessions may lack catalogMeta — keep reads safe.
  if (!g[STORE_KEY].catalogMeta) g[STORE_KEY].catalogMeta = new Map();
  return g[STORE_KEY];
}

export function readTtl<T>(
  cache: Map<string, TtlEntry<unknown>>,
  key: string,
  ttlMs: number,
): T | null {
  const hit = cache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at >= ttlMs) {
    cache.delete(key);
    return null;
  }
  return hit.value as T;
}

export function writeTtl<T>(
  cache: Map<string, TtlEntry<unknown>>,
  key: string,
  value: T,
): void {
  cache.set(key, { at: Date.now(), value });
}

export function dedupeGet<T>(key: string, run: () => Promise<T>): Promise<T> {
  const { inflight } = eaCaches();
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;

  const promise = run().finally(() => {
    if (inflight.get(key) === promise) inflight.delete(key);
  });
  inflight.set(key, promise);
  return promise;
}
